import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import Icon from './icons.jsx'
import { useApp } from '../context/AppContext.jsx'
import { ToastContainer } from './UI.jsx'
import { PeriodPicker } from './UI.jsx'
import ClientSwitcher from './ClientSwitcher.jsx'
import OrganizationSwitcher from './OrganizationSwitcher.jsx'
import Logo, { LOGO_MARK_TITAM } from './Logo.jsx'
// Dados mockados temporários até carregar da API
const suggestions = []

// ─────────────────────────────────────────────────────────────────────────────
// NAVEGAÇÃO POR MÓDULOS DE NEGÓCIO
//
// Estrutura em árvore. Cada nó pode ter `path` (navega) e/ou `children`
// (expande/recolhe). Renderizada recursivamente por <NavNode />, então novos
// módulos (Estratégia, Comercial, Financeiro, Meta Ads, Instagram) ganham
// funcionalidade própria apenas trocando o `path`/adicionando `children`.
//
// Marketing agrupa canais independentes. Google Ads aponta para o dashboard
// pago; SEO fica no mesmo nível como módulo orgânico próprio.
// ─────────────────────────────────────────────────────────────────────────────
// Estratégia, Agentes, Estoque e Jurídico ficam temporariamente ocultos da
// sidebar (módulos ainda em desenvolvimento) — ver App.jsx para o mesmo
// bloqueio nas rotas correspondentes.
const NAV = [
  { id: 'dashboard', label: 'Dashboard', icon: 'ads', path: '/dashboard' },
  {
    id: 'marketing', label: 'Marketing', icon: 'megaphone',
    children: [
      {
        id: 'google-ads', label: 'Google Ads', icon: 'ads', path: '/google-ads',
        children: [
          { id: 'campanhas', label: 'Campanhas', icon: 'chart', path: '/google-ads/campanhas' },
          { id: 'leads', label: 'Leads', icon: 'leads', path: '/google-ads/leads' },
          { id: 'keywords', label: 'Palavras-chave', icon: 'key', path: '/google-ads/palavras-chave' },
          { id: 'location-devices', label: 'Localização / Dispositivos', icon: 'geo', path: '/google-ads/localizacao-dispositivos' },
        ],
      },
      {
        id: 'meta-ads', label: 'Meta Ads', icon: 'meta', path: '/meta-ads',
        children: [
          { id: 'meta-campanhas', label: 'Campanhas', icon: 'chart', path: '/meta-ads/campanhas' },
          { id: 'meta-location-devices', label: 'Localização / Dispositivos', icon: 'geo', path: '/meta-ads/localizacao-dispositivos' },
          { id: 'meta-conversoes', label: 'Conversões', icon: 'zap', path: '/meta-ads/conversoes' },
          { id: 'meta-utms', label: 'UTMs', icon: 'link', path: '/meta-ads/utms' },
        ],
      },
      { id: 'instagram', label: 'Instagram', icon: 'star', path: '/instagram' },
      { id: 'facebook', label: 'Facebook', icon: 'users', path: '/facebook' },
      { id: 'seo', label: 'SEO', icon: 'search', path: '/seo' },
      { id: 'mapa', label: 'Mapa', icon: 'map', path: '/mapa' },
    ],
  },
  { id: 'atendimento', label: 'Atendimento', icon: 'headset', path: '/atendimento' },
  {
    id: 'comercial', label: 'Comercial', icon: 'dollar_circle', path: '/comercial',
    children: [
      { id: 'comercial-prospeccao', label: 'Prospecção', icon: 'search', path: '/comercial/prospeccao' },
      { id: 'comercial-empresas', label: 'Empresas', icon: 'briefcase', path: '/comercial/empresas' },
      { id: 'comercial-leads', label: 'Leads', icon: 'leads', path: '/comercial/leads' },
      { id: 'comercial-pipelines', label: 'Pipelines', icon: 'chart', path: '/comercial/pipelines' },
      { id: 'comercial-produtos', label: 'Produtos', icon: 'sales', path: '/comercial/produtos' },
      { id: 'comercial-servicos', label: 'Serviços', icon: 'sales', path: '/comercial/servicos' },
    ],
  },
  // Agenda é módulo próprio (issue #78), não submódulo do Comercial: ganhará
  // páginas que não são assunto do Comercial (disponibilidade, links públicos
  // de agendamento, integrações de calendário). Fica logo abaixo do Comercial
  // por afinidade de uso — quem marca reunião vem do fluxo comercial.
  { id: 'agenda', label: 'Agenda', icon: 'calendar', path: '/agenda' },
  // Automações (issue #137): fluxos gatilho → condição → ação sobre o CRM.
  { id: 'automacoes', label: 'Automações', icon: 'zap', path: '/automacoes' },
  {
    // Como Marketing → Google Ads: o próprio nó abre a Dashboard do módulo;
    // os filhos são só os submódulos (sem um item "Dashboard" separado).
    id: 'financeiro', label: 'Financeiro', icon: 'chart', path: '/financeiro/dashboard',
    children: [
      { id: 'financeiro-pagar', label: 'Contas a pagar', icon: 'arrow_up', path: '/financeiro/contas-pagar' },
      { id: 'financeiro-receber', label: 'Contas a receber', icon: 'arrow_down', path: '/financeiro/contas-receber' },
      { id: 'financeiro-saldo', label: 'Saldo', icon: 'currency', path: '/financeiro/saldo' },
      { id: 'financeiro-clientes', label: 'Clientes', icon: 'users', path: '/financeiro/clientes' },
      { id: 'financeiro-fornecedores', label: 'Fornecedores', icon: 'sales', path: '/financeiro/fornecedores' },
    ],
  },
  {
    // Como Financeiro: o próprio nó abre a Dashboard do módulo — não existe um
    // submódulo "Dashboard". O path é a raiz do módulo, então o casamento de
    // rota ativa tem de ser exato (ver matchNode).
    id: 'operacao', label: 'Operação', icon: 'folder', path: '/operacao',
    children: [
      { id: 'operacao-tarefas', label: 'Tarefas', icon: 'timeline', path: '/operacao/tarefas' },
      { id: 'operacao-projetos', label: 'Projetos', icon: 'folder', path: '/operacao/projetos' },
      { id: 'operacao-equipes', label: 'Equipes', icon: 'users', path: '/operacao/equipes' },
    ],
  },
]

// Rota atual casa com o nó? Folha casa também nas sub-rotas (`/operacao/projetos`
// segue ativo em `/operacao/projetos/:id`); nó COM FILHOS casa só exatamente,
// senão o pai roubaria o destaque do filho — é o caso de `/operacao`, que é
// prefixo de todos os submódulos. Antes isso era um caso especial cravado para
// `/google-ads`; a regra generalizada corrige junto o `/meta-ads`.
function matchNode(node, currentPath) {
  if (!node.path) return false
  if (currentPath === node.path) return true
  if (node.children?.length) return false
  return currentPath.startsWith(`${node.path}/`)
}

// Coleta os ids de todos os ancestrais cujo descendente casa com o path atual,
// para abrir automaticamente os submenus que contêm a rota ativa.
function ancestorsOf(nodes, currentPath, trail = []) {
  for (const node of nodes) {
    const selfMatch = matchNode(node, currentPath)
    if (node.children) {
      const found = ancestorsOf(node.children, currentPath, [...trail, node.id])
      if (found) return found
    }
    if (selfMatch) return trail
  }
  return null
}

// Primeira rota navegável do nó (a própria ou do 1º descendente que tenha
// path). Usada quando a sidebar está recolhida: como os submenus ficam
// escondidos, clicar num grupo (ex.: Marketing) precisa levar a algum lugar
// em vez de só alternar um submenu invisível.
function firstPathOf(node) {
  if (node.path) return node.path
  for (const child of node.children || []) {
    const p = firstPathOf(child)
    if (p) return p
  }
  return null
}

function NavNode({ node, currentPath, expanded, onToggle, collapsed, onNavigate, activeTrail, level = 0 }) {
  const navigate = useNavigate()
  const hasChildren = (node.children?.length || 0) > 0
  const isOpen = expanded.has(node.id)
  const isActive = matchNode(node, currentPath)
  // Módulo que contém a rota atual em algum descendente: fica azul como
  // "Comercial" no mock, mesmo sem ser ele próprio a rota ativa.
  const isSection = !isActive && activeTrail.has(node.id)
  const showSub = hasChildren && isOpen && !collapsed

  const rowClass = [
    'sb-item',
    isActive ? 'active' : '',
    isSection ? 'section' : '',
    showSub ? 'open' : '',
  ].filter(Boolean).join(' ')
  // Recolhida: só ícones. `title` vira tooltip nativo do rótulo escondido.
  const rowTitle = collapsed ? node.label : undefined

  // Caret: alterna o submenu sem navegar (preventDefault corta a navegação do <a>).
  // Só na sidebar ABERTA: recolhida os rótulos/submenus somem, a seta fica inútil.
  // Sempre `chevron_d` — o CSS gira 180° quando aberto, para a virada ser animada.
  const caret = hasChildren && !collapsed && (
    <span
      className="sb-caret"
      style={{ marginLeft: node.badge > 0 ? 6 : 'auto' }}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(node.id) }}
    >
      <Icon n="chevron_d" s={16} sw={2} />
    </span>
  )

  // Nível 0 leva ícone; subitens são só texto (mock), com um ponto azul na
  // coluna do ícone quando ativos.
  const inner = (
    <>
      {level === 0
        ? <span className="sb-item-icon"><Icon n={node.icon} s={18} sw={1.6} /></span>
        : <span className="sb-item-dot" aria-hidden="true" />}
      <span className="sb-label">{node.label}</span>
      {node.badge > 0 && <span className="sb-badge">{node.badge}</span>}
      {caret}
    </>
  )

  // Nós com rota viram <Link> (renderiza <a href> real): habilita o menu de
  // contexto "Abrir em nova guia", Ctrl+clique e botão do meio nativamente.
  // Nós sem rota (grupos como Marketing) permanecem como botão de expandir.
  const row = node.path ? (
    <Link
      to={node.path}
      className={rowClass}
      title={rowTitle}
      aria-current={isActive ? 'page' : undefined}
      aria-expanded={hasChildren ? isOpen : undefined}
      onClick={(e) => {
        // Cliques com modificador / botão do meio: deixa o navegador abrir a guia.
        if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
        // Navegar por um módulo da sidebar sempre a reexpande (só Configurações recolhe).
        onNavigate?.()
        if (hasChildren && (node.id !== 'operacao' || !isOpen)) onToggle(node.id)
      }}
    >
      {inner}
    </Link>
  ) : (
    <div
      className={rowClass}
      title={rowTitle}
      role="button"
      tabIndex={0}
      aria-expanded={hasChildren ? isOpen : undefined}
      onClick={() => {
        // Recolhida: submenu fica escondido — navega para o 1º item do grupo
        // em vez de alternar algo invisível (evita ficar "preso" na sidebar).
        if (collapsed && hasChildren) {
          const p = firstPathOf(node)
          if (p) { onNavigate?.(); navigate(p); return }
        }
        if (hasChildren) onToggle(node.id)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          if (collapsed && hasChildren) {
            const p = firstPathOf(node)
            if (p) { onNavigate?.(); navigate(p); return }
          }
          if (hasChildren) onToggle(node.id)
        }
      }}
    >
      {inner}
    </div>
  )

  // O submenu fica sempre montado para a altura poder animar (grid 0fr→1fr).
  // Fechado, o CSS aplica `visibility:hidden` — some da ordem de tabulação.
  const sub = hasChildren && (
    <div className={`sb-sub-wrap${showSub ? ' open' : ''}`} aria-hidden={!showSub}>
      <div className="sb-sub">
        {node.children.map(child => (
          <NavNode
            key={child.id}
            node={child}
            currentPath={currentPath}
            expanded={expanded}
            onToggle={onToggle}
            collapsed={collapsed}
            onNavigate={onNavigate}
            activeTrail={activeTrail}
            level={level + 1}
          />
        ))}
      </div>
    </div>
  )

  // Módulo de topo com filhos: cabeçalho e submenu dentro de um bloco único,
  // que ganha o fundo azulado arredondado quando aberto (mock: "Comercial").
  if (level === 0 && hasChildren) {
    return (
      <div className={`sb-group${showSub ? ' open' : ''}`}>
        {row}
        {sub}
      </div>
    )
  }

  return (
    <>
      {row}
      {sub}
    </>
  )
}

function Sidebar({ currentPath, mobileOpen, onCloseMobile }) {
  // Submenus abertos. Inicia com os ancestrais da rota atual já expandidos.
  const [expanded, setExpanded] = useState(() => new Set(ancestorsOf(NAV, currentPath) || []))
  // Ancestrais da rota atual: pintam o módulo em azul (estado "seção ativa").
  const activeTrail = new Set(ancestorsOf(NAV, currentPath) || [])
  const toggleNode = (id) => setExpanded(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })
  // Ao mudar de rota, garante que os submenus que contêm a rota ativa fiquem abertos.
  useEffect(() => {
    const anc = ancestorsOf(NAV, currentPath)
    if (anc?.length) setExpanded(prev => new Set([...prev, ...anc]))
  }, [currentPath])
  const {
    sidebarCollapsed, sidebarForcedCollapsed, setSidebarCollapsed, setSidebarForcedCollapsed,
  } = useApp()
  // Recolhimento efetivo = preferência manual OU forçado pela página (calendário
  // / área de Configurações). No drawer mobile a barra sempre abre por
  // completo (ignora o "recolhida") — é uma sobreposição temporária, não faz
  // sentido mostrar só ícones nela.
  const collapsed = (sidebarCollapsed || sidebarForcedCollapsed) && !mobileOpen

  // Ao clicar no botão, o usuário assume o controle: libera o recolhimento
  // forçado pela página e passa a valer só a preferência manual. Assim é
  // possível expandir a sidebar mesmo dentro das Configurações.
  const handleToggle = () => {
    const next = !collapsed
    setSidebarForcedCollapsed(false)
    setSidebarCollapsed(next)
  }
  // Navegar por um módulo da sidebar reexpande sempre: libera o recolhimento
  // forçado (Configurações) e a preferência manual. Centralizado aqui — as
  // páginas não precisam de tratamento próprio. Só Configurações recolhe (via
  // SettingsLayout), pois seu link fica fora da NAV. No mobile, navegar
  // também fecha o drawer.
  const expandOnNavigate = () => {
    setSidebarForcedCollapsed(false)
    setSidebarCollapsed(false)
    onCloseMobile?.()
  }
  return (
    <div className={`sidebar${collapsed ? ' collapsed' : ''}${mobileOpen ? ' mobile-open' : ''}`}>
      <div className="sb-top">
        <div className="sb-logo">
          <Logo height={12} />
          <span className="sb-logo-suffix">· CRM</span>
        </div>
        {/* Recolhida, o wordmark não cabe: entra o símbolo (mesmo PNG do
            favicon). Os dois ficam sempre montados e trocam por opacidade,
            para a transição acompanhar a da largura. */}
        <img className="sb-logo-mark" src={LOGO_MARK_TITAM} alt="TITAM" aria-hidden={!collapsed} />
        <button
          type="button"
          className="sb-mobile-close"
          onClick={onCloseMobile}
          aria-label="Fechar menu"
        >
          <Icon n="close" s={18} sw={1.8} />
        </button>
      </div>

      <nav className="sb-nav" aria-label="Navegação principal">
        <div className="sb-section">
          {NAV.map(node => (
            <NavNode
              key={node.id}
              node={node}
              currentPath={currentPath}
              expanded={expanded}
              onToggle={toggleNode}
              collapsed={collapsed}
              onNavigate={expandOnNavigate}
              activeTrail={activeTrail}
            />
          ))}
        </div>
      </nav>

      {/* Rodapé só com o recolher: perfil vive no avatar da topbar e Suporte
          mora em Configurações → Conexões/Suporte. Sem navegação duplicada. */}
      <div className="sb-bottom">
        {/* Recolher/expandir — no rodapé, como no mock. Some no drawer mobile,
            que não tem estado "recolhido" próprio. */}
        <button
          className="sb-item sb-collapse-row"
          type="button"
          onClick={handleToggle}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
          title={collapsed ? 'Expandir menu' : 'Recolher menu'}
        >
          <span className="sb-item-icon">
            <Icon n={collapsed ? 'chevron_r' : 'chevron_l'} s={18} sw={1.6} />
          </span>
          <span className="sb-label">Recolher</span>
        </button>
      </div>
    </div>
  )
}

function Topbar({ title, breadcrumb, showPeriod = false, right, onOpenMobileMenu }) {
  // Perfil da topbar: mesma foto/nome/cargo da sidebar, vindos do usuário
  // autenticado. Sem foto salva, cai nas iniciais (user.avatar) — igual à
  // sidebar, para os dois blocos nunca divergirem.
  const { user, avatarUrl } = useApp()

  return (
    <div className="topbar">
      {/* Agrupados num único item flex: com justify-content:space-between na
          .topbar, hambúrguer/logo/título soltos como filhos diretos ficariam
          espalhados em vez de colados à esquerda (um por par de espaço). */}
      <div className="tb-left-group">
        {/* Só existe (via CSS) abaixo de 768px — abre o drawer da sidebar. */}
        <button
          type="button"
          className="tb-menu-btn"
          onClick={onOpenMobileMenu}
          aria-label="Abrir menu"
        >
          <Icon n="menu" s={20} sw={1.8} />
        </button>
        {/* Com a sidebar (onde mora a logo) escondida fora da tela no mobile, a
            topbar fica sem nenhuma marca — mostra o símbolo aqui só ≤768px
            (mesmo PNG do favicon/sidebar recolhida) pra manter identidade. */}
        <img className="tb-logo-mark" src={LOGO_MARK_TITAM} alt="TITAM" />
        <div className="tb-left">
          <div className="tb-title">{title}</div>
          {breadcrumb?.length > 0 && (
            <div className="tb-breadcrumb">
              {breadcrumb.map((item, i) => {
                const isStr = typeof item === 'string'
                const label = isStr ? item : item.label
                const to = isStr ? null : item.to
                const last = i === breadcrumb.length - 1
                return (
                  <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {to && !last
                      ? <Link to={to}>{label}</Link>
                      : <span style={last ? { color: 'var(--text)', fontWeight: 500 } : undefined}>{label}</span>}
                    {!last && <span className="tb-breadcrumb-sep">/</span>}
                  </span>
                )
              })}
            </div>
          )}
        </div>
      </div>
      <div className="tb-right">
        <OrganizationSwitcher />
        <ClientSwitcher />
        {showPeriod && <PeriodPicker />}
        {right}
        <Link className="tb-user" to="/configuracoes/perfil" title="Perfil e configurações">
          <span className="tb-user-avatar">
            {avatarUrl
              ? <img src={avatarUrl} alt="" />
              : (user?.avatar || '?')}
          </span>
          <span className="tb-user-info">
            <span className="tb-user-name">{user?.name || '...'}</span>
            <span className="tb-user-role">{user?.roleLabel || 'Membro'}</span>
          </span>
          <span className="tb-user-caret">
            <Icon n="chevron_d" s={14} c="var(--ter)" />
          </span>
        </Link>
      </div>
    </div>
  )
}

export default function Layout({ children, title, breadcrumb, showPeriod, topbarRight }) {
  const location = useLocation()
  const { sidebarCollapsed, sidebarForcedCollapsed } = useApp()
  const collapsed = sidebarCollapsed || sidebarForcedCollapsed
  // Drawer da sidebar em telas ≤768px — estado só de UI, não persiste (some ao
  // navegar ou trocar de rota, igual a qualquer overlay mobile).
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  useEffect(() => { setMobileNavOpen(false) }, [location.pathname])
  // ESC fecha + trava o scroll do body atrás do drawer, mesmo padrão do
  // KpiDrawer/SupportModal.
  useEffect(() => {
    if (!mobileNavOpen) return
    const onEsc = (e) => e.key === 'Escape' && setMobileNavOpen(false)
    document.addEventListener('keydown', onEsc)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onEsc)
      document.body.style.overflow = prevOverflow
    }
  }, [mobileNavOpen])

  return (
    <div className="app">
      <Sidebar
        currentPath={location.pathname}
        mobileOpen={mobileNavOpen}
        onCloseMobile={() => setMobileNavOpen(false)}
      />
      {mobileNavOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setMobileNavOpen(false)}
          aria-hidden="true"
        />
      )}
      <div className={`main${collapsed ? ' collapsed' : ''}`}>
        <Topbar
          title={title}
          breadcrumb={breadcrumb}
          showPeriod={showPeriod}
          right={topbarRight}
          onOpenMobileMenu={() => setMobileNavOpen(true)}
        />
        <div className="page">{children}</div>
      </div>
      <ToastContainer />
    </div>
  )
}
